/* Copyright (c) 2021-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { FormProvider, useForm } from "react-hook-form";
import { FlexContainer } from "@nordcloud/gnui";
import { isNil } from "~/tools";
import { baselineTagToShow, getFlatPlanEntities } from "~/utils";
import { PlanErrors, PlanField } from "../../../constants";
import { usePlanWizard } from "../../../PlanProvider";
import { PlanEntity } from "../../../types";
import { useStepState } from "../../StepProvider";
import { ActionItemEdit } from "../ActionItemEdit";
import { EditableActionName } from "./EditableActionName";

type Props<T> = {
  items: T[];
  onDelete: (item: T) => void;
  batchId: string;
};

export function DraggableBatchActionsList<T extends PlanEntity>({
  items,
  onDelete,
  batchId,
}: Props<T>) {
  const { setPlanData, setSelectedEntity, planData } = usePlanWizard();
  const {
    stepState: { selectedEntity },
    updateStepState,
  } = useStepState();

  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const setPlanErrors = (planError: PlanErrors) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.ERRORS]: [
        ...new Set([...(prevPlanData[PlanField.ERRORS] ?? []), planError]),
      ],
    }));
  };

  const clearPlanError = (planError: PlanErrors) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.ERRORS]: prevPlanData[PlanField.ERRORS]?.filter(
        (errors) => errors !== planError
      ),
    }));
  };

  const handlePlanErrors = () => {
    if (Object.keys(errors).length > 0) {
      setPlanErrors(PlanErrors.PLAN_ACTION_NAMES);
    } else {
      clearPlanError(PlanErrors.PLAN_ACTION_NAMES);
    }
  };

  const formMethods = useForm();
  const {
    formState: { errors },
    clearErrors,
  } = formMethods;

  const flatEntities = useMemo(
    () =>
      getFlatPlanEntities(
        planData?.[PlanField.PLAN_SETTINGS]?.planEntities || []
      ),
    [planData]
  );

  return (
    <FlexContainer as="ul" direction="column" alignItems="stretch">
      {(items ?? []).map((i, index) => {
        const entityListId = i.listId;
        if (isNil(entityListId)) {
          return null;
        }

        const baselineTag = baselineTagToShow(i, flatEntities);
        const item = {
          ...i,
          icon: "dragAndDrop",
          editableName: (
            <FormProvider {...formMethods}>
              <EditableActionName
                item={i}
                isEditing={editingItemId === entityListId}
                tag={baselineTag}
                onEditClick={() => setEditingItemId(entityListId)}
                handlePlanErrors={handlePlanErrors}
                onEditComplete={() => setEditingItemId(null)}
              />
            </FormProvider>
          ),
        };

        return (
          <Draggable
            key={entityListId}
            draggableId={entityListId}
            index={index}
          >
            {(provided) => {
              return (
                <ActionItemEdit
                  aria-label={item.name}
                  isActive={selectedEntity?.listId === entityListId}
                  {...{ item }}
                  {...{ provided }}
                  editableName={item.editableName}
                  onMouseDown={() => {
                    setSelectedEntity?.(entityListId);
                    updateStepState({
                      selectedEntity: {
                        ...item,
                        batchId: batchId,
                      },
                    });
                  }}
                  onDelete={() => {
                    clearErrors(entityListId);
                    handlePlanErrors();
                    onDelete(item);
                  }}
                />
              );
            }}
          </Draggable>
        );
      })}
    </FlexContainer>
  );
}
